import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Divider, Typography, useMediaQuery } from "@material-ui/core";
import {
  CustomAsyncAutocomplete,
  CustomButton,
  CustomIconButton,
  ReskinningInputs,
} from "../../../../../Components";

import { dealStatus } from "../../../../../Enums/dealStatus.Enum";

import {
  GetLookupItemsByLookupTypeName,
  UpdateAgentCommissionTransaction,
} from "../../../../../Services";

import { useSelectedTheme, useTranslate } from "../../../../../Hooks";

import useStyles from "./styles";

import {
  ArrowLeftIcon,
  CloseXIcon,
  PlusIcon,
} from "../../../../../assets/icons";
import AgentCommissionNestedMenu from "./AgentCommissionNestedMenu/AgentCommissionNestedMenu";

import { useVerticalNav } from "../../../../../Contexts/VerticalNavContext";
import { getStatus } from "../../util";
import WarningDialog from "../../WarningDialog/WarningDialog";

function AgentCommissionSideMenu({
  onClose,
  selectedTransaction,
  handleSideMenuOpen,
  handleAddTab,
  tabs,
  setTabs,
  refetchdata,
}) {
  const { isDarkMode } = useSelector((state) => state.theme);
  const styles = useStyles({ isDarkMode });
  const { translate } = useTranslate("AgentCommissionView");
  const isSmallScreen = useMediaQuery("(max-width:960px)");

  const [formValue, setFormValue] = useState({
    unitTransactionAgentId: null,
    transactionID: null,
    dealStatus: null,
    adminFees: 0,
    agentCommissionRate: null,
    deduction: null,
    listingCommission: null,
    telesalesCommission: null,
    coordinatorCommission: null,
    tlCommissionRate: null,
    smCommissionRate: null,
    invoices: [],
  });
  const [error, setError] = useState({});
  const [hasUpdated, setHasUpdated] = useState(false);
  const { setAlertBoxContent } = useVerticalNav();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (selectedTransaction) {
      setFormValue({
        unitTransactionAgentId:
          selectedTransaction?.unitTransactionAgentId || 0,
        transactionID: selectedTransaction?.transactionID || null,
        dealStatus:
          {
            lookupItemName: dealStatus[selectedTransaction?.dealStatus]?.label,
            lookupItemId: dealStatus[selectedTransaction?.dealStatus]?.id,
          } || null,
        adminFees: selectedTransaction?.adminFees || 0,
        agentCommissionRate: selectedTransaction?.agentCommissionRate || 0,
        deduction: selectedTransaction?.deduction || 0,
        listingCommission: selectedTransaction?.listingCommission || 0,
        telesalesCommission: selectedTransaction?.telesalesCommission || 0,
        coordinatorCommission: selectedTransaction?.coordinatorCommission || 0,
        tlCommissionRate: selectedTransaction?.tlCommissionRate || 0,
        smCommissionRate: selectedTransaction?.smCommissionRate || 0,
      });
    }
  }, [selectedTransaction]);

  const {
    theme: { palette },
  } = useSelectedTheme();

  const validateFields = () => {
    if (selectedTransaction) {
      const errors = {};
      if (formValue.agentCommissionRate > 100)
        errors.agentCommissionRate = translate("agent-commission-rate-error");
      if (formValue.adminFees > selectedTransaction.finalNetAmount)
        errors.adminFees = translate("admin-fees-error");
      if (formValue.deduction > selectedTransaction.finalNetAmount)
        errors.deduction = translate("deduction-amount-error");
      if (formValue.listingCommission > selectedTransaction.finalNetAmount)
        errors.listingCommission = translate("listing-commission-error");
      if (formValue.smCommissionRate > 100)
        errors.smCommissionRate = translate("sm-commission-rate-error");
      if (formValue.telesalesCommission > selectedTransaction.finalNetAmount)
        errors.telesalesCommission = translate("telesales-commission-error");
      if (formValue.tlCommissionRate > 100)
        errors.tlCommissionRate = translate("tl-commission-rate-error");
      if (formValue.coordinatorCommission > selectedTransaction.finalNetAmount)
        errors.coordinatorCommission = translate(
          "coordinator-commission-error"
        );
      if (
        Object.values(formValue).some(
          (val) => val === null || Number.isNaN(val)
        )
      ) {
        errors.form = translate("form-fields-required");
      }
      setError(errors);
    }
  };
  //handle save on small screens without nested menu save button
  const hasInvoiceMobile =
    Object.values(tabs[0] || {}).length > 2 && isSmallScreen;
  const handleUpdateTransaction = async () => {
    const transaction = { ...selectedTransaction };
    delete transaction.agentId;
    const res = await UpdateAgentCommissionTransaction(
      {
        ...transaction,
        ...formValue,
        dealStatus: getStatus(formValue.dealStatus?.lookupItemName, 0),
        invoices: hasInvoiceMobile
          ? tabs.map((tab) => ({
              agentAchievementCommissionInvoiceId:
                tab.agentAchievementCommissionInvoiceId || 0,
              invoicesNumber: tab.invoicesNumber || 0,
              voucherNumber: tab.voucherNumber || 0,
              invoiceStatus: getStatus(tab.invoiceStatus?.lookupItemName, 0),
              collectionStatus: getStatus(
                tab.collectionStatus?.lookupItemName,
                0
              ),
              paymentStatus: getStatus(tab.paymentStatus?.lookupItemName, 0),
              agencyFeesRate: tab.agencyFeesRate || 0,
              agentCommissionRate: tab.agentCommissionRate || 0,
              deduction: tab.deduction || 0,
              totalDealExpense: tab.totalDealExpense || 0,
              adminFees: tab.adminFees || 0,
              listingCommission: tab.listingCommission || 0,
              telesalesCommission: tab.telesalesCommission || 0,
              coordinatorCommission: tab.coordinatorCommission || 0,
              tlCommissionRate: tab.tlCommissionRate || 0,
              smCommissionRate: tab.smCommissionRate || 0,
            }))
          : transaction?.invoices || [],
      },
      selectedTransaction.agentId
    );
    if (!(res?.status && res.status !== 200)) {
      refetchdata();
      return setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate("transaction-updated-successfully"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    }
    return setAlertBoxContent({
      display: true,
      variant: "error",
      title: translate("transaction-updated-error"),
      onClose: () => {
        setAlertBoxContent(null);
      },
    });
  };

  useEffect(() => {
    validateFields();
  }, [formValue]);

  return (
    <Box className={styles.root}>
      <Box className={styles.headingContainer}>
        <Box className={styles.menuTitleContainer}>
          <Typography className={styles.menuHeading} variant="h2">
            {translate("transaction-details")}
          </Typography>
          <Typography component={"p"} className={styles.subtitle}>
            {translate("manage-team-members")}
          </Typography>
        </Box>

        <CustomIconButton
          variant="text"
          size="md"
          boxShadow="none"
          color="tertiaryColor"
          hideHoverBg
          onClick={onClose}
        >
          <CloseXIcon
            width="20"
            height="20"
            fill={palette.button.tertiaryColor_fg}
          />
        </CustomIconButton>
      </Box>
      <Divider className={styles.titleDivider} />
      {isSmallScreen && (
        <>
          <AgentCommissionNestedMenu
            selectedTransaction={selectedTransaction}
            isOpen={isSmallScreen}
            tabs={tabs}
            setTabs={setTabs}
          />
          <Divider className={styles.divider} />
        </>
      )}
      <Box className={styles.detailsContainer}>
        <Typography className={styles.sectionHeader}>
          {translate("deal-status-and-admin")}
        </Typography>
        <Divider className={styles.divider} />
        <CustomAsyncAutocomplete
          labelValue={translate("deal-status")}
          selectedValues={formValue.dealStatus}
          rootCustomClasses={styles.inputwrapper}
          className={styles.autoCompleteInput}
          onChange={(_e, value) => {
            setFormValue({
              ...formValue,
              dealStatus: value,
            });
            if (
              getStatus(formValue.dealStatus?.lookupItemName, 0) !==
              selectedTransaction?.dealStatus
            ) {
              setHasUpdated(true);
            }
          }}
          disabled={!selectedTransaction}
          mapper={(data) => {
            return data.map(({ lookupItemName, lookupItemId }) => ({
              lookupItemName,
              lookupItemId,
            }));
          }}
          placeholder={translate("status")}
          onFetchData={GetLookupItemsByLookupTypeName}
          filter={{
            lookUpName: "AgentAchievementCommissionDealStatus",
            pageSize: 25,
            pageIndex: 1,
          }}
          displayLabel={(option) => option?.lookupItemName ?? ""}
          renderOption={(option) => option?.lookupItemName}
          chipsLabel={(option) => {
            return option?.lookupItemName;
          }}
          getOptionSelected={(option, value) =>
            option?.lookupItemId === value?.lookupItemId
          }
        />
        <Divider className={styles.divider} />
        <ReskinningInputs
          labelValue={translate("admin-fees")}
          value={formValue.adminFees}
          rootCustomClasses={styles.inputwrapper}
          textFieldWrapperClassName={styles.fieldWrapper}
          type="number"
          min={0}
          withNumberFormat
          onInputChanged={(e) => {
            setFormValue({ ...formValue, adminFees: e.target.value });
            if (e.target.value !== selectedTransaction?.adminFees) {
              setHasUpdated(true);
            }
          }}
          error={error.adminFees}
          isWithError={!!error.adminFees}
          helperText={error.adminFees}
        />
        <Divider className={styles.divider} />
        <Typography className={styles.sectionHeader}>
          {translate("commission-details")}
        </Typography>
        <Divider className={styles.divider} />
        <ReskinningInputs
          labelValue={translate("agent-commission-rate")}
          inputPlaceholder={translate("percent-eg")}
          rootCustomClasses={styles.inputwrapper}
          type="number"
          value={formValue.agentCommissionRate}
          startAdornment="%"
          min={0}
          max={100}
          withNumberFormat
          onInputChanged={(e) => {
            setFormValue({
              ...formValue,
              agentCommissionRate: e.target.value,
            });
            if (e.target.value !== selectedTransaction?.agentCommissionRate) {
              setHasUpdated(true);
            }
          }}
          error={error.agentCommissionRate}
          isWithError={!!error.agentCommissionRate}
          helperText={error.agentCommissionRate}
        />
        <Divider className={styles.divider} />
        <ReskinningInputs
          labelValue={translate("deduction-amount")}
          inputPlaceholder={translate("aed-amount")}
          rootCustomClasses={styles.inputwrapper}
          type="number"
          value={formValue.deduction}
          min={0}
          withNumberFormat
          onInputChanged={(e) => {
            setFormValue({ ...formValue, deduction: e.target.value });
            if (e.target.value !== selectedTransaction?.deduction) {
              setHasUpdated(true);
            }
          }}
          error={error.deduction}
          isWithError={!!error.deduction}
          helperText={error.deduction}
        />
        <Divider className={styles.divider} />
        <Typography className={styles.sectionHeader}>
          {translate("other-commissions")}
        </Typography>
        <Divider className={styles.divider} />
        <ReskinningInputs
          labelValue={translate("listing-commission")}
          inputPlaceholder={translate("aed-amount")}
          rootCustomClasses={styles.inputwrapper}
          type="number"
          value={formValue.listingCommission}
          onInputChanged={(e) => {
            setFormValue({ ...formValue, listingCommission: e.target.value });
            if (e.target.value !== selectedTransaction?.listingCommission) {
              setHasUpdated(true);
            }
          }}
          withNumberFormat
          error={error.listingCommission}
          isWithError={!!error.listingCommission}
          helperText={error.listingCommission}
        />
        <Divider className={styles.divider} />
        <ReskinningInputs
          labelValue={translate("telesales-commission")}
          inputPlaceholder={translate("aed-amount")}
          rootCustomClasses={styles.inputwrapper}
          type="number"
          value={formValue.telesalesCommission}
          onInputChanged={(e) => {
            setFormValue({ ...formValue, telesalesCommission: e.target.value });
            if (e.target.value !== selectedTransaction?.telesalesCommission) {
              setHasUpdated(true);
            }
          }}
          withNumberFormat
          error={error.telesalesCommission}
          isWithError={!!error.telesalesCommission}
          helperText={error.telesalesCommission}
        />
        <Divider className={styles.divider} />
        <ReskinningInputs
          labelValue={translate("coordinator-commission")}
          inputPlaceholder={translate("aed-amount")}
          rootCustomClasses={styles.inputwrapper}
          onInputChanged={(e) => {
            setFormValue({
              ...formValue,
              coordinatorCommission: e.target.value,
            });
            if (e.target.value !== selectedTransaction?.coordinatorCommission) {
              setHasUpdated(true);
            }
          }}
          type="number"
          value={formValue.coordinatorCommission}
          withNumberFormat
          error={error.coordinatorCommission}
          isWithError={!!error.coordinatorCommission}
          helperText={error.coordinatorCommission}
        />
        <Divider className={styles.divider} />
        <ReskinningInputs
          labelValue={translate("tl-commission-rate")}
          inputPlaceholder={translate("percent-eg")}
          rootCustomClasses={styles.inputwrapper}
          startAdornment="%"
          type="number"
          value={formValue.tlCommissionRate}
          onInputChanged={(e) => {
            setFormValue({ ...formValue, tlCommissionRate: e.target.value });
            if (e.target.value !== selectedTransaction?.tlCommissionRate) {
              setHasUpdated(true);
            }
          }}
          withNumberFormat
          error={error.tlCommissionRate}
          isWithError={!!error.tlCommissionRate}
          helperText={error.tlCommissionRate}
        />
        <Divider className={styles.divider} />
        <ReskinningInputs
          labelValue={translate("sm-commission-rate")}
          inputPlaceholder={translate("percent-eg")}
          rootCustomClasses={styles.inputwrapper}
          type="number"
          value={formValue.smCommissionRate}
          onInputChanged={(e) => {
            setFormValue({ ...formValue, smCommissionRate: e.target.value });
            if (e.target.value !== selectedTransaction?.smCommissionRate) {
              setHasUpdated(true);
            }
          }}
          startAdornment="%"
          error={error.smCommissionRate}
          isWithError={!!error.smCommissionRate}
          helperText={error.smCommissionRate}
        />
        {!isSmallScreen && (
          <Box className={styles.addInvoicesContainer}>
            <Typography
              className={styles.sectionHeader}
              style={{ marginBottom: 20 }}
            >
              {translate("invoices")}
            </Typography>
            <Divider className={styles.divider} />

            <Box className={styles.addInvoiceContainer}>
              <Typography className={styles.numberOfInvoicesTitle}>
                {translate("number-of-invoices")}
              </Typography>
              <CustomIconButton
                variant="text"
                size="md"
                boxShadow="none"
                color="tertiaryColor"
                hideHoverBg
                onClick={() => {
                  if (hasUpdated) {
                    setIsDialogOpen(true);
                  } else {
                    handleAddTab();
                    handleSideMenuOpen();
                  }
                }}
                classes={styles.addInvoiceButton}
              >
                <Box display="flex" alignItems="center" gridGap="4px">
                  {tabs?.length > 0 ? (
                    <>
                      <ArrowLeftIcon
                        width="20"
                        height="20"
                        fill={palette.button.tertiaryColor_fg}
                      />
                      <Typography className={styles.viewNumberOfInvoicesTitle}>
                        {translate("view-added-invoices")}
                      </Typography>
                      <Typography className={styles.numberOfInvoicesAdded}>
                        {`${tabs?.length} `}{" "}
                        <Typography
                          className={styles.invoicesSubText}
                          component="span"
                        >
                          {translate("invoices-added")}
                        </Typography>
                      </Typography>
                    </>
                  ) : (
                    <>
                      <PlusIcon
                        width="20"
                        height="20"
                        fill={palette.button.tertiaryColor_fg}
                      />
                      <Typography className={styles.addInvoiceText}>
                        {translate("add-invoice")}
                      </Typography>
                    </>
                  )}
                </Box>
              </CustomIconButton>
            </Box>
          </Box>
        )}
      </Box>
      <Box className={styles.buttonContainer}>
        <CustomButton
          boxShadow="xs"
          size="md"
          variant={"outlined"}
          color={"secondary"}
          onClick={onClose}
        >
          {translate("cancel")}
        </CustomButton>
        <CustomButton
          size="md"
          variant={"primary"}
          color={"primary"}
          disabled={Object.keys(error).length > 0}
          onClick={() => {
            handleUpdateTransaction();
            setHasUpdated(false);
          }}
        >
          {translate("add-invoice-details")}
        </CustomButton>
      </Box>
      <WarningDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          handleAddTab();
          handleSideMenuOpen();
        }}
        onConfirmAction={() => {
          handleUpdateTransaction();
          setHasUpdated(false);
          handleAddTab();
          handleSideMenuOpen();
        }}
      />
    </Box>
  );
}

export default React.memo(AgentCommissionSideMenu, (prevProps, nextProps) => {
  // Only re-render if selectedTransaction or open/close state changes
  return (
    prevProps.selectedTransaction === nextProps.selectedTransaction &&
    prevProps.tabs === nextProps.tabs &&
    prevProps.isOpen === nextProps.isOpen
  );
});
